import { Stack, Pivot, PivotItem } from "@fluentui/react";

import styles from "./AnalysisPanel.module.css";

import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
//import { ThoughtProcess } from "./ThoughtProcess";
import { MarkdownViewer } from "../MarkdownViewer";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "../../api";
import { useLogin, getToken } from "../../authConfig";
import { useState, useEffect } from "react";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

const VIDEO_CITATION_URL = "https://academy.thalassaemia.org.cy/course/index.php?categoryid=3";

const isVideoCitation = (citation: string | undefined): boolean => {
    if (!citation) {
        return false;
    }
    // Drop the #t=..min (or any) fragment before checking the extension.
    const pathWithoutHash = citation.split("#")[0].toLowerCase();
    return pathWithoutHash.endsWith(".srt") || pathWithoutHash.endsWith(".vtt");
};

const GTR_2025_CITATION = {
    file: "gtr2025.pdf",
    text: "Eleftheriou, A., & Angastiniotis, M. (2025). Global Thalassaemia Review 2025 - Thalassaemia International Federation's Perspective. Thalassaemia International Federation.",
    url: "https://thalassaemia.org.cy/download/global-thalassaemia-review-2025/"
}

const getExternalCitation = (citation: string | undefined) => {
    if (!citation) {
        return undefined;
    }

    const pathWithoutHash = citation.split("#")[0].toLowerCase();
    return pathWithoutHash.endsWith(GTR_2025_CITATION.file) ? GTR_2025_CITATION : undefined;
}

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState("");

    const client = useLogin ? useMsal().instance : undefined;

    const fetchCitation = async () => {
        const token = client ? await getToken(client) : undefined;
        const externalCitation = getExternalCitation(activeCitation);

        // Video citations have no backing file to download; they link out instead.
        if (activeCitation && !isVideoCitation(activeCitation) && !externalCitation) {
            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const originalHash = activeCitation.indexOf("#") ? activeCitation.split("#")[1] : "";
            const response = await fetch(activeCitation, {
                method: "GET",
                headers: await getHeaders(token)
            });
            const citationContent = await response.blob();
            let citationObjectUrl = URL.createObjectURL(citationContent);
            // Add hash back to the new blob URL
            if (originalHash) {
                citationObjectUrl += "#" + originalHash;
            }
            setCitation(citationObjectUrl);
        }
    };
    useEffect(() => {
        setCitation("");
        fetchCitation();
    }, [activeCitation]);

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        const externalCitation = getExternalCitation(activeCitation);

        if (externalCitation) {
            return (
                <div style={{ padding: "16px" }}>
                    <p>{externalCitation.text}</p>
                    <a href={externalCitation.url} target="_blank" rel="noopener noreferrer">
                        Open source
                    </a>
                </div>
            );
        }

        if (isVideoCitation(activeCitation)) {
            return (
                <div style={{ padding: "16px" }}>
                    <p>This citation refers to a video lecture from the course Genetic Counselling for Healthcare Professionals Engaged in the Field of Haemoglobin Disorders, available on TIF e-Academy. Enrollment in the course is free.</p>
                    <a href={VIDEO_CITATION_URL} target="_blank" rel="noopener noreferrer">
                        ▶ Open in Thalassaemia Academy
                    </a>
                </div>
            );
        }

        const fileExtension = activeCitation.split(".").pop()?.toLowerCase();
        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md":
                return <MarkdownViewer src={activeCitation} />;
            default:
                return <iframe title="Citation" src={citation} width="100%" height={citationHeight} />;
        }
    };

    return (
        <Pivot
            className={className}
            selectedKey={activeTab}
            onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
        >
            {/*<PivotItem
                itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                headerText="Thought process"
                headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
            >
                <ThoughtProcess thoughts={answer.context.thoughts || []} />
            </PivotItem>*/}
            <PivotItem
                itemKey={AnalysisPanelTabs.SupportingContentTab}
                headerText="Supporting content"
                headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
            >
                <SupportingContent supportingContent={answer.context.data_points} />
            </PivotItem>
            <PivotItem
                itemKey={AnalysisPanelTabs.CitationTab}
                headerText="Citation"
                headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
            >
                {renderFileViewer()}
            </PivotItem>
        </Pivot>
    );
};
